import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';

import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import SEO from './components/seo/SEO';
import PerformanceOptimizer from './components/system/PerformanceOptimizer';
import TrailingSlashRedirect from './components/system/TrailingSlashRedirect';
import ErrorBoundary from './components/system/ErrorBoundary';
import Analytics from './components/system/Analytics';
import AppRoutes, { showsGlobalFooter } from './routes';

// Scroll to top on route change. useLayoutEffect + instant behavior: the jump
// happens after the new page's DOM lands but before it paints, so there is no
// flash of the old scroll position and nothing can interrupt a smooth scroll
// mid-way (the old deferred/smooth version regularly lost that race).
//
// The critical CSS sets height:100% on html/body, which makes <body> the real
// scroll container (html's overflow-x:hidden stops propagation to the
// viewport) — so window.scrollTo alone is a no-op here. Reset every candidate.
export const ScrollToTop = () => {
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname]);

  return null;
};

/**
 * Application shell: theme + menu state, global chrome (navbar, footer,
 * analytics, default SEO), and the route table from ./routes.
 */
const AppContent: React.FC = () => {
  // Initialize dark mode from localStorage to prevent flash
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      return saved ? JSON.parse(saved) : true;
    }
    return true;
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const [isSlowDevice, setIsSlowDevice] = useState(false);
  const location = useLocation();

  // Detect low-end devices so the performance optimizer can back off
  useEffect(() => {
    const checkDevice = () => {
      const mobile = window.innerWidth <= 768;
      const slow =
        mobile &&
        ((navigator as any).hardwareConcurrency <= 4 ||
          (navigator as any).deviceMemory <= 4 ||
          ((navigator as any).connection && (navigator as any).connection.effectiveType?.includes('2g')));
      setIsSlowDevice(Boolean(slow));
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  // Persist dark mode changes to localStorage and the <html> class
  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Close the mobile menu on navigation
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className={darkMode ? 'dark' : ''}>
      <div className="min-h-screen" style={{ minHeight: '100vh' }}>
        <PerformanceOptimizer
          enableCodeSplitting={true}
          enableTaskScheduling={true}
          enableResourceHints={!isSlowDevice}
          enableUnusedCodeRemoval={true}
        />

        {/* Default SEO, overridden by page-specific SEO */}
        <SEO
          title="Talal Alkhaled (Talal Al-Khaled) | Computer Engineer"
          description="Welcome to the official website of Talal Alkhaled (Talal Al-Khaled), a passionate Computer Engineer from Michigan State University and developer building innovative solutions."
          keywords="Talal Alkhaled, Talal Al-Khaled, Talal Al khaled, طلال الخالد, Computer Engineering, Michigan State University, MSU, Portfolio, Personal Website"
        />

        <Analytics />

        {/* Navbar rendered outside relative container to ensure proper fixed positioning */}
        <Navbar darkMode={darkMode} setDarkMode={setDarkMode} menuOpen={menuOpen} setMenuOpen={setMenuOpen} />

        <div>
          <TrailingSlashRedirect />

          <main>
            <AppRoutes darkMode={darkMode} />
          </main>

          {/* Global footer — skipped on pages that render their own */}
          {showsGlobalFooter(location.pathname) && <Footer darkMode={darkMode} />}
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <AppContent />
    </ErrorBoundary>
  );
}

export default App;
